import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { 
  Home, 
  Calendar, 
  Plus, 
  User, 
  LogOut, 
  Menu, 
  X,
  Video,
  BarChart3,
  Users as UsersIcon,
  Ticket,
  ClipboardList,
  Clapperboard,
  Download,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { isAdmin, isAdminOrSupport, isCustomerSupport, isVideoRecorder } from '../utils/roles';
import NotificationBell from './NotificationBell';

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const support = user && isAdminOrSupport(user.role);
  const supportOnly = user && isCustomerSupport(user.role);
  const recorder = user && isVideoRecorder(user.role);
  const showNotificationBell = !!(supportOnly || recorder);

  const showManagementNav = !!user && isAdmin(user.role);

  const managementRoutes = showManagementNav
    ? [
        { name: 'User Management', href: '/users' },
        { name: 'Video processing', href: '/video-processing-settings' },
      ]
    : [];

  // Sessions list / ad-hoc recording are admin-only; support & recorders work via tickets.
  const showSessionsNav = !!user && !supportOnly && !recorder;

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    ...(showSessionsNav
      ? [
          { name: 'ETR Videos', href: '/sessions', icon: Calendar },
          { name: 'New ETR Video', href: '/sessions/new', icon: Plus },
        ]
      : []),
    { name: 'My Tickets', href: '/my-tickets', icon: ClipboardList },
    ...(recorder ? [{ name: 'Install recorder', href: '/install-recorder', icon: Download }] : []),
    ...(support ? [{ name: 'Tickets', href: '/tickets', icon: Ticket }] : []),
    { name: 'Profile', href: '/profile', icon: User },
  ];

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar */}
      <div 
        className={`fixed inset-0 z-50 md:hidden transition-opacity duration-300 ease-in-out ${
          sidebarOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        {/* Backdrop with fade animation */}
        <div 
          className={`fixed inset-0 bg-gray-900 transition-opacity duration-300 ${
            sidebarOpen ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={() => setSidebarOpen(false)}
        />
        
        {/* Sidebar with slide animation */}
        <div 
          className={`fixed left-0 top-0 bottom-0 w-full max-w-xs bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-10 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex h-full flex-col">
            {/* Header with close button */}
            <div className="flex items-center justify-between px-4 pt-4 pb-4 border-b border-secondary-200">
              <div className="flex items-center">
                <Video className="h-8 w-8 text-primary-600" />
                <span className="ml-2 text-xl font-bold text-gray-900">ETR Video Studio</span>
              </div>
              <button
                type="button"
                className="rounded-md p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-colors"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto pt-4 pb-4">
              <nav className="space-y-1 px-2">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-3 py-3 text-base font-medium rounded-lg transition-colors ${
                        isActive(item.href)
                          ? 'bg-primary-100 text-gray-900'
                          : 'text-gray-700 hover:bg-secondary-100 hover:text-gray-900'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon className="mr-4 h-6 w-6 flex-shrink-0" />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
              
              {/* Management Section — admin, administrator, video_admin (matches API RoleUtils.IsAdmin) */}
              {showManagementNav && (
                <div className="mt-4 px-2 space-y-1">
                <Link
                  to="/users"
                  className={`group flex items-center px-3 py-3 text-base font-medium rounded-lg transition-colors ${
                    isActive('/users')
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <UsersIcon className="mr-4 h-6 w-6 flex-shrink-0" />
                  User Management
                </Link>
                <Link
                  to="/video-processing-settings"
                  className={`group flex items-center px-3 py-3 text-base font-medium rounded-lg transition-colors ${
                    isActive('/video-processing-settings')
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Clapperboard className="mr-4 h-6 w-6 flex-shrink-0" />
                  Video processing
                </Link>
              </div>
              )}
            </div>
            
            {/* Footer with user info and logout */}
            <div className="flex-shrink-0 border-t border-secondary-200 p-4 bg-secondary-50">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-base font-medium text-gray-700 truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-sm font-medium text-gray-500 truncate">
                    {user?.email}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSidebarOpen(false);
                    logout();
                  }}
                  className="ml-4 rounded-md p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-white shadow">
          <div className="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
            <div className="flex flex-shrink-0 items-center px-4">
              <div className="flex items-center">
                <Video className="h-8 w-8 text-primary-600" />
                <span className="ml-2 text-xl font-bold text-gray-900">ETR Video Studio</span>
              </div>
            </div>
            <nav className="mt-5 flex-1 space-y-1 bg-white px-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                      isActive(item.href)
                        ? 'bg-primary-100 text-gray-900'
                        : 'text-gray-600 hover:bg-secondary-100 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="mr-3 h-6 w-6 flex-shrink-0" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
            
            {/* Management Section — admin, administrator, video_admin (matches API RoleUtils.IsAdmin) */}
            {showManagementNav && (
              <div className="mt-5 px-2 space-y-1">
                <Link
                  to="/users"
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive('/users')
                      ? 'bg-primary-100 text-gray-900'
                      : 'text-gray-600 hover:bg-secondary-100 hover:text-gray-900'
                  }`}
                >
                  <UsersIcon className="mr-3 h-6 w-6 flex-shrink-0" />
                  User Management
                </Link>
                <Link
                  to="/video-processing-settings"
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive('/video-processing-settings')
                      ? 'bg-primary-100 text-gray-900'
                      : 'text-gray-600 hover:bg-secondary-100 hover:text-gray-900'
                  }`}
                >
                  <Clapperboard className="mr-3 h-6 w-6 flex-shrink-0" />
                  Video processing
                </Link>
              </div>
            )}
          </div>
          <div className="flex flex-shrink-0 border-t border-secondary-200 p-4">
            <div className="group block w-full flex-shrink-0">
              <div className="flex items-center">
                <div className="ml-3 w-full">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs font-medium text-gray-500 group-hover:text-gray-700">
                        {user?.email}
                      </p>
                    </div>
                    <button
                      onClick={logout}
                      className="rounded-md p-1 text-gray-400 hover:text-gray-500"
                      title="Logout"
                    >
                      <LogOut className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="md:pl-64 flex flex-col flex-1">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-white/95 backdrop-blur border-b border-secondary-200">
          <button
            type="button"
            className="border-r border-secondary-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex flex-1 justify-between px-4">
            <div className="flex flex-1">
              <div className="flex w-full items-center justify-between">
                <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                  {[...navigation, ...managementRoutes].find((item) => isActive(item.href))?.name ||
                    'Dashboard'}
                </h1>
                <div className="flex items-center space-x-3">
                  {showNotificationBell ? <NotificationBell /> : null}
                  <div className="hidden sm:flex items-center space-x-2 text-sm text-gray-500">
                    <BarChart3 className="h-4 w-4" />
                    <span>Progress Tracking</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-visible">
          <div className="py-6 overflow-visible">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 overflow-visible">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

    </div>
  );
};

export default Layout;

